import type {
  Audience,
  Asset,
  AssetScope,
  AuditLog,
  Campaign,
  CampaignInput,
  CampaignStatus,
  CampaignWithRefs,
  ConsentEvent,
  ConsoleEnvironment,
  Creative,
  CreativeApprovalInput,
  CreativeInput,
  CreativeServingInput,
  CurrentUser,
  Guide,
  GuideDraft,
  GuideDraftRequest,
  GuideInput,
  GuideWithUrl,
  NetworkLocation,
  NetworkLocationInput,
  Placement,
  PlacementInput,
  PortalCampaignsResponse,
  ReportingSummary,
  Sponsor,
  SponsorCreateInput,
  SponsorInput,
  SponsorUser,
  SponsorUserInviteInput,
  SponsorWithStats,
  TeamAdminInput,
  TeamInviteInput,
  TeamMember,
  VideoUploadFinalizeResponse,
  VideoUploadInitiateResponse,
} from '@dmn/shared';
import { AUTH_MODE, DEV_SPONSOR, DEV_USER, getFirebaseAuth } from './firebase';

/**
 * Typed client for the console API.
 *
 * Request and response shapes come from `@dmn/shared`, the same module the
 * server validates against — so a contract change breaks the build here rather
 * than surfacing as a runtime surprise in a campaign form.
 */

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  if (AUTH_MODE === 'emulator') {
    return DEV_SPONSOR ? { 'x-dmn-sponsor': DEV_SPONSOR } : { 'x-dmn-dev-user': DEV_USER };
  }

  const user = getFirebaseAuth().currentUser;
  if (!user) return {};
  return { authorization: `Bearer ${await user.getIdToken()}` };
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
  /** `/v1/console` for staff, `/v1/portal` for a sponsor. Defaults to console. */
  namespace?: '/v1/console' | '/v1/portal';
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(
    `${BASE}${options.namespace ?? '/v1/console'}${path}`,
    window.location.origin
  );
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body ? { 'content-type': 'application/json' } : {}),
      ...(await authHeaders()),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as { error: { code: string; message: string; details?: unknown } }).error
        : null;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? `Request failed with ${response.status}`,
      error?.details
    );
  }

  return payload as T;
}

export const api = {
  videoUploads: {
    initiate: (campaignId: string, signal?: AbortSignal) =>
      request<VideoUploadInitiateResponse>('/video-uploads', {
        method: 'POST',
        body: { campaignId },
        signal,
      }),
    finalize: (uploadId: string, signal?: AbortSignal) =>
      request<VideoUploadFinalizeResponse>(
        `/video-uploads/${encodeURIComponent(uploadId)}/finalize`,
        {
          method: 'POST',
          signal,
        }
      ),
    /** Signed GCS PUT: never send the console's authorization headers to storage. */
    upload: (
      uploadUrl: string,
      file: File,
      onProgress: (fraction: number) => void,
      signal: AbortSignal
    ) =>
      new Promise<void>((resolve, reject) => {
        if (signal.aborted) {
          reject(new DOMException('Upload cancelled', 'AbortError'));
          return;
        }
        const xhr = new XMLHttpRequest();
        const abort = () => xhr.abort();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', 'video/mp4');
        xhr.timeout = 15 * 60_000;
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) onProgress(event.loaded / event.total);
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else
            reject(
              new ApiError(xhr.status, 'upload_failed', 'Video upload failed. Retry the upload.')
            );
        });
        xhr.addEventListener('error', () =>
          reject(
            new ApiError(0, 'network', 'Video upload failed. Check your connection and retry.')
          )
        );
        xhr.addEventListener('timeout', () =>
          reject(new ApiError(0, 'timeout', 'Video upload timed out. Retry the upload.'))
        );
        xhr.addEventListener('abort', () =>
          reject(new DOMException('Upload cancelled', 'AbortError'))
        );
        xhr.addEventListener('loadend', () => signal.removeEventListener('abort', abort));
        signal.addEventListener('abort', abort, { once: true });
        xhr.send(file);
      }),
  },
  me: () => request<{ user: CurrentUser }>('/me').then((r) => r.user),

  /**
   * Deployment facts, so the console can name the right guide host rather than
   * hard-coding one. Separate from `me()` because callers want one or the other,
   * and the underlying request is cached by TanStack Query either way.
   */
  environment: () => request<{ environment: ConsoleEnvironment }>('/me').then((r) => r.environment),

  sponsors: {
    list: () => request<{ sponsors: SponsorWithStats[] }>('/sponsors').then((r) => r.sponsors),
    create: (body: SponsorCreateInput) =>
      request<{ sponsor: Sponsor }>('/sponsors', { method: 'POST', body }).then((r) => r.sponsor),
    update: (id: string, body: SponsorInput) =>
      request<{ sponsor: Sponsor }>(`/sponsors/${id}`, { method: 'PUT', body }).then(
        (r) => r.sponsor
      ),
    /** Refused while any campaign still points at the sponsor. */
    remove: (id: string) => request<void>(`/sponsors/${id}`, { method: 'DELETE' }),

    users: {
      list: (sponsorId: string) =>
        request<{ users: SponsorUser[] }>(`/sponsors/${sponsorId}/users`).then((r) => r.users),
      /**
       * Grants access and nothing else — no link, no mail. The sponsor asks for
       * their own at `/portal/signin`.
       */
      invite: (sponsorId: string, body: SponsorUserInviteInput) =>
        request<{ user: SponsorUser; created: boolean }>(`/sponsors/${sponsorId}/users`, {
          method: 'POST',
          body,
        }),
      revoke: (sponsorId: string, uid: string) =>
        request<void>(`/sponsors/${sponsorId}/users/${uid}`, { method: 'DELETE' }),
    },
  },

  campaigns: {
    list: () => request<{ campaigns: CampaignWithRefs[] }>('/campaigns').then((r) => r.campaigns),
    get: (id: string) =>
      request<{ campaign: CampaignWithRefs; creatives: Creative[] }>(`/campaigns/${id}`),
    create: (body: CampaignInput) =>
      request<{ campaign: Campaign }>('/campaigns', { method: 'POST', body }).then(
        (r) => r.campaign
      ),
    update: (id: string, body: CampaignInput) =>
      request<{ campaign: Campaign }>(`/campaigns/${id}`, { method: 'PUT', body }).then(
        (r) => r.campaign
      ),
    setStatus: (id: string, status: CampaignStatus) =>
      request<{ campaign: Campaign }>(`/campaigns/${id}/status`, {
        method: 'POST',
        body: { status },
      }).then((r) => r.campaign),
  },

  creatives: {
    create: (body: CreativeInput) =>
      request<{ creative: Creative }>('/creatives', { method: 'POST', body }).then(
        (r) => r.creative
      ),
    /**
     * Returns `forkedFrom` when the server created a new version instead of
     * editing in place — which it does for any already-approved creative.
     */
    update: (id: string, body: CreativeInput) =>
      request<{ creative: Creative; forkedFrom?: string }>(`/creatives/${id}`, {
        method: 'PUT',
        body,
      }).then((r) => r.creative),
    setApproval: (id: string, body: CreativeApprovalInput) =>
      request<{ creative: Creative }>(`/creatives/${id}/approval`, { method: 'POST', body }).then(
        (r) => r.creative
      ),
    /** Takes an approved creative in or out of rotation. Not an approval change. */
    setServing: (id: string, body: CreativeServingInput) =>
      request<{ creative: Creative }>(`/creatives/${id}/serving`, { method: 'POST', body }).then(
        (r) => r.creative
      ),
  },

  team: {
    /**
     * Every account in the project, not only the admins — "why can't they get
     * in" is the question this view exists to answer, and the answer is usually
     * an account with no claim.
     */
    list: () =>
      request<{
        members: TeamMember[];
        bootstrapEmails: string[];
        allowedDomains: string[];
      }>('/team'),
    /**
     * Grants access to an address that may not have an account yet.
     *
     * No email is sent — the account is created with the claim on it, so the
     * person signs in with Google and is already in.
     */
    invite: (body: TeamInviteInput) =>
      request<{ member: TeamMember; created: boolean }>('/team/invite', {
        method: 'POST',
        body,
      }),
    setAdmin: (uid: string, body: TeamAdminInput) =>
      request<{ member: TeamMember }>(`/team/${uid}/admin`, { method: 'POST', body }).then(
        (r) => r.member
      ),
  },

  guideAi: {
    /** Whether this environment has a model configured. */
    status: () => request<{ available: boolean; model: string | null }>('/guides/ai-status'),
    /**
     * Writes a page from a brief. Returns a proposal, saves nothing — the normal
     * create/update routes stay the only write path.
     */
    generate: (body: GuideDraftRequest) =>
      request<{ draft: GuideDraft }>('/guides/generate', { method: 'POST', body }).then(
        (r) => r.draft
      ),
  },

  placements: {
    list: () => request<{ placements: Placement[] }>('/placements').then((r) => r.placements),
    create: (body: PlacementInput) =>
      request<{ placement: Placement }>('/placements', { method: 'POST', body }).then(
        (r) => r.placement
      ),
    update: (id: string, body: PlacementInput) =>
      request<{ placement: Placement }>(`/placements/${id}`, { method: 'PUT', body }).then(
        (r) => r.placement
      ),
  },

  locations: {
    list: () => request<{ locations: NetworkLocation[] }>('/locations').then((r) => r.locations),
    /** Refused while guide pages or campaign targeting still reference it. */
    remove: (id: string) => request<void>(`/locations/${id}`, { method: 'DELETE' }),
    /**
     * Upsert, keyed on the dashboard's location id. Same endpoint a dashboard
     * sync job would call, so re-sending an unchanged record is a no-op.
     */
    upsert: (body: NetworkLocationInput) =>
      request<{ location: NetworkLocation }>(`/locations/${body.id}`, {
        method: 'PUT',
        body,
      }).then((r) => r.location),
  },

  assets: {
    /**
     * Uploads an image.
     *
     * `XMLHttpRequest` rather than `fetch` for one reason: it reports upload
     * progress. `fetch` cannot, and a 2 MB image over a poor connection with no
     * feedback reads as a broken form. The body is the raw file — no multipart,
     * so no parser on either side.
     */
    upload: (
      file: File,
      scope: AssetScope,
      ownerId: string,
      onProgress?: (fraction: number) => void,
      signal?: AbortSignal
    ) =>
      new Promise<Asset>((resolve, reject) => {
        if (signal?.aborted) {
          reject(new DOMException('Upload cancelled', 'AbortError'));
          return;
        }
        const url = new URL(`${BASE}/v1/console/assets`, window.location.origin);
        url.searchParams.set('scope', scope);
        url.searchParams.set('ownerId', ownerId);

        const request = new XMLHttpRequest();
        request.open('POST', url.toString());
        request.responseType = 'json';

        request.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) onProgress?.(event.loaded / event.total);
        });

        request.addEventListener('load', () => {
          const payload: unknown = request.response;

          if (request.status >= 200 && request.status < 300) {
            resolve((payload as { asset: Asset }).asset);
            return;
          }

          const error =
            payload && typeof payload === 'object' && 'error' in payload
              ? (payload as { error: { code: string; message: string } }).error
              : null;
          reject(
            new ApiError(
              request.status,
              error?.code ?? 'upload_failed',
              error?.message ?? `Upload failed with ${request.status}`
            )
          );
        });

        request.addEventListener('error', () =>
          reject(new ApiError(0, 'network', 'Upload failed — check your connection'))
        );
        request.addEventListener('abort', () =>
          reject(new ApiError(0, 'aborted', 'Upload cancelled'))
        );
        const abort = () => {
          request.abort();
          reject(new DOMException('Upload cancelled', 'AbortError'));
        };
        signal?.addEventListener('abort', abort, { once: true });
        request.addEventListener('loadend', () => signal?.removeEventListener('abort', abort));

        void authHeaders()
          .then((headers) => {
            if (signal?.aborted) return;
            for (const [name, value] of Object.entries(headers)) {
              request.setRequestHeader(name, value);
            }
            request.setRequestHeader('content-type', file.type);
            request.send(file);
          })
          .catch((error: unknown) => {
            signal?.removeEventListener('abort', abort);
            reject(error);
          });
      }),

    /** Turns a stored path into a URL, so an existing image can be previewed. */
    resolve: (path: string) =>
      request<{ url: string }>('/assets/resolve', { query: { path } }).then((r) => r.url),
  },

  guides: {
    list: () => request<{ guides: GuideWithUrl[] }>('/guides').then((r) => r.guides),
    create: (body: GuideInput) =>
      request<{ guide: Guide }>('/guides', { method: 'POST', body }).then((r) => r.guide),
    update: (id: string, body: GuideInput) =>
      request<{ guide: Guide }>(`/guides/${id}`, { method: 'PUT', body }).then((r) => r.guide),
    remove: (id: string) => request<void>(`/guides/${id}`, { method: 'DELETE' }),
  },

  reporting: {
    get: (from: string, to: string, campaignId?: string, audience?: Audience) =>
      request<ReportingSummary>('/reporting', { query: { from, to, campaignId, audience } }),
    aggregate: () =>
      request<{ processed: number; bucketsTouched: number; skipped: number }>(
        '/reporting/aggregate',
        { method: 'POST' }
      ),
  },

  consentEvents: (limit = 50) =>
    request<{ events: ConsentEvent[] }>('/consent-events', { query: { limit } }).then(
      (r) => r.events
    ),

  auditLogs: (limit = 50) =>
    request<{ logs: AuditLog[] }>('/audit-logs', { query: { limit } }).then((r) => r.logs),
};

/**
 * The sponsor's own view of `/v1/portal` — read-only, and structurally unable
 * to reach `/v1/console`: every call here goes through the `namespace` option
 * on `request`, never the console base path.
 */
export const portalApi = {
  campaigns: () => request<PortalCampaignsResponse>('/campaigns', { namespace: '/v1/portal' }),
  reporting: (from: string, to: string, campaignId?: string, audience?: Audience) =>
    request<ReportingSummary>('/reporting', {
      namespace: '/v1/portal',
      query: { from, to, campaignId, audience },
    }),
};
